import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route } from 'react-router-dom';

import 'intro.js/introjs.css';
import 'react-datepicker/dist/react-datepicker.css';

import { EnvVars } from '../environment';
import { Admin } from './new/pages/admin/organisations';
import { AdminManageOrganisation } from './new/pages/admin/organisations/:organisationId';
import { OrganisationRuns } from './new/pages/admin/organisations/:organisationId/runs';
import { DocumentsExplorer } from './new/pages/matters/:matterId/docs';
import { Subsets } from './new/pages/matters/:matterId/subsets';
import { Usage } from './new/pages/usage';
import { ErrorBoundary } from '@/components/atoms/ErrorBoundary';
import { Toaster } from '@/components/atoms/Toaster';
import { TooltipProvider } from '@/components/atoms/Tooltip';
import { RelativityCallback } from '@/components/oauth/RelativityCallback';
import { HeaderBar } from '@/components/organisms/HeaderBar';
import { AppSidebar } from '@/components/organisms/Sidebar';
import { SidebarProvider } from '@/components/organisms/Sidebar/Sidebar';
import { UserProvider } from '@/Contexts/User';
import { Home } from '@/new/pages';
import { AdminManageUsers } from '@/new/pages/admin/organisations/:organisationId/users';
import { ClientMaterials } from '@/new/pages/client-materials';
import { Layout } from '@/new/pages/layout';
import { ManageUsers } from '@/new/pages/manage-users';
import { Matters } from '@/new/pages/matters';
import { MatterOverview } from '@/new/pages/matters/:matterId';
import { SubsetOverview } from '@/new/pages/matters/:matterId/subsets/:subsetId';
import { Documents } from '@/new/pages/matters/:matterId/subsets/:subsetId/docs';
import { Document } from '@/new/pages/matters/:matterId/subsets/:subsetId/docs/:docId';
import { Entities } from '@/new/pages/matters/:matterId/subsets/:subsetId/entities';
import { Facts } from '@/new/pages/matters/:matterId/subsets/:subsetId/facts';
import { Kim } from '@/new/pages/matters/:matterId/subsets/:subsetId/kim';
import { KimThread } from '@/new/pages/matters/:matterId/subsets/:subsetId/kim/:threadId';
import { Realtime } from '@/new/pages/matters/:matterId/subsets/:subsetId/realtime';
import { Transcript } from '@/new/pages/matters/:matterId/subsets/:subsetId/realtime/:transcriptId';
import { SubsetCreator } from '@/new/pages/matters/:matterId/subsets/new';
import { ReleaseNotes } from '@/new/pages/release-notes';
import Screens from '@/screens';

const queryClient = new QueryClient();

function App() {
  return (
    <ErrorBoundary>
      <RequiredAuthProvider
        authUrl={EnvVars.AUTH_URL}
        displayWhileLoading={
          <div className="flex h-screen w-screen items-center justify-center">
            <FontAwesomeIcon icon={faCircleNotch} className="h-4 w-4 animate-spin" />
          </div>
        }
        displayIfLoggedOut={<RedirectToLogin />}
      >
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <BrowserRouter>
              <TooltipProvider>
                <Toaster />
                <Routes>
                  <Route path="oauth/relativity/callback" element={<RelativityCallback />} />

                  {/* Old frontend */}
                  <Route
                    path="app/*"
                    element={
                      <SidebarProvider defaultOpen={false}>
                        <div className="flex h-screen w-full">
                          <AppSidebar />
                          <div className="flex h-full grow flex-col overflow-x-auto">
                            <HeaderBar />
                            <div className="h-[calc(100vh-3.25rem)] flex-1">
                              <Screens />
                            </div>
                          </div>
                        </div>
                      </SidebarProvider>
                    }
                  />

                  {/* New frontend */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />

                    <Route path="client-materials" element={<ClientMaterials />} />
                    <Route path="release-notes" element={<ReleaseNotes />} />

                    <Route path="usage" element={<Usage />} />
                    <Route path="manage-users" element={<ManageUsers />} />

                    <Route path="admin">
                      <Route path="organisations">
                        <Route index element={<Admin />} />
                        <Route path=":organisationId">
                          <Route index element={<AdminManageOrganisation />} />
                          <Route path="users" element={<AdminManageUsers />} />
                          <Route path="runs" element={<OrganisationRuns />} />
                        </Route>
                      </Route>
                    </Route>

                    <Route path="matters">
                      <Route index element={<Matters />} />
                      <Route path=":matterId">
                        <Route index element={<MatterOverview />} />
                        <Route path="docs" element={<DocumentsExplorer />} />
                        <Route path="subsets">
                          <Route index element={<Subsets />} />
                          <Route path="new" element={<SubsetCreator />} />

                          <Route path=":subsetId">
                            <Route index element={<SubsetOverview />} />
                            <Route path="facts" element={<Facts />} />
                            <Route path="docs">
                              <Route index element={<Documents />} />
                              <Route path=":docId" element={<Document />} />
                            </Route>
                            <Route path="kim">
                              <Route index element={<Kim />} />
                              <Route path=":threadId" element={<KimThread />} />
                            </Route>
                            <Route path="realtime">
                              <Route index element={<Realtime />} />
                              <Route path=":transcriptId" element={<Transcript />} />
                            </Route>
                            <Route path="entities" element={<Entities />} />
                          </Route>
                        </Route>
                      </Route>
                    </Route>
                  </Route>
                </Routes>
              </TooltipProvider>
            </BrowserRouter>
          </UserProvider>
        </QueryClientProvider>
      </RequiredAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
